// The two sounds of jumbo reactions (#714): the thud when a jumbo reaction lands, and the splat when a hold runs too
// long. Both files are in shared/sounds/ (the same files every client ships). They play through one AudioContext
// that a press creates and resumes: a browser lets a sound start later (the splat comes 6 s into a press) only when a
// gesture started its audio. Every call is a no-op where audio isn't there (tests, a blocked context).
import splatUrl from "../../shared/sounds/splat.mp3";
import thudUrl from "../../shared/sounds/thud.wav";

type Name = "splat" | "thud";
const URLS: Record<Name, string> = { splat: splatUrl, thud: thudUrl };

let ctx: AudioContext | null = null;
const buffers = new Map<Name, AudioBuffer | Promise<AudioBuffer | null>>();

function context(): AudioContext | null {
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx ??= new AC();
    if (ctx.state === "suspended") void ctx.resume().catch(() => {});
    return ctx;
  } catch {
    return null;
  }
}

/** Fetches and decodes one sound, once. */
function load(name: Name, ac: AudioContext): Promise<AudioBuffer | null> {
  const have = buffers.get(name);
  if (have) return Promise.resolve(have);
  const p = fetch(URLS[name])
    .then((r) => r.arrayBuffer())
    .then((b) => ac.decodeAudioData(b))
    .then((buf) => {
      buffers.set(name, buf);
      return buf;
    })
    .catch(() => {
      buffers.delete(name); // try again on the next press
      return null;
    });
  buffers.set(name, p);
  return p;
}

/** Call on a press: starts the audio context and loads both sounds, so they are ready when the hold needs them. */
export function primeJumboSounds() {
  const ac = context();
  if (!ac) return;
  void load("splat", ac);
  void load("thud", ac);
}

function play(name: Name, delayS = 0) {
  const ac = context();
  if (!ac) return;
  void load(name, ac).then((buf) => {
    if (!buf) return;
    try {
      const src = ac.createBufferSource();
      src.buffer = buf;
      src.connect(ac.destination);
      src.start(ac.currentTime + delayS);
    } catch {
      // no sound is not an error
    }
  });
}

export const playSplat = () => play("splat");
/** `delayS`: seconds until the impact, so the thud lands with the emoji. */
export const playThud = (delayS = 0) => play("thud", delayS);
